/* eslint-disable react/no-unescaped-entities */
import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, X, Sparkles, ArrowRight, Mail, Twitter, Linkedin, BarChart3, Zap, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CLAIMS } from "@/config/claims";

// Reusable comparison framework — every /vs/<competitor> page renders from a
// single data shape. Honest, two-column, no fake numbers. We highlight what THEY
// do well so the page reads as a fair comparison Google + readers will trust.
const COMPETITORS = {
  "taka-ai": {
    name: "Taka.ai",
    site: "taka.ai",
    intro: "Taka.ai is a sharp AI content tool for small-business brand consistency. Creatara is a full content-to-revenue stack — generation + scheduling + auto-publishing + affiliate tracking + Stripe + SEO infrastructure.",
    summary_quote: "If all you need is on-brand content for a local business, Taka is a great pick. If your goal is to actually earn money from your content — affiliates, paid offers, scheduled multi-platform posting, attribution — Creatara bakes that full stack in.",
    one_liner: "Taka makes content. Creatara makes content + revenue.",
    table: [
      { feature: "AI text generation (X, LinkedIn, IG, FB, TikTok)", pj: true, them: true, note: "Both use GPT-class models" },
      { feature: "AI image generation per post", pj: true, them: true },
      { feature: "Brand Kit (name, colours, logo, tone, audience, do/don't lists)", pj: true, them: true, note: "Taka pioneered it; we shipped parity in Feb 2026" },
      { feature: "Complete Post bundle (caption + image + hashtags + alt text + first-comment)", pj: true, them: true },
      { feature: "Auto-publishes to your social accounts at the scheduled time", pj: true, them: false, note: "Creatara: X, LinkedIn, Bluesky & Telegram (FB/IG pending Meta review). Taka exports — you paste manually" },
      { feature: "Scheduled posts with timezone-aware delivery", pj: true, them: false },
      { feature: "Email reminder fallback for non-connected platforms", pj: true, them: false },
      { feature: "Sora 2 native video clip generation", pj: true, them: false },
      { feature: "Affiliate link generator with custom slugs", pj: true, them: false },
      { feature: "Per-platform UTM attribution dashboard", pj: true, them: false },
      { feature: "Revenue Campaigns wizard (one product theme → posts + email + blog draft)", pj: true, them: false },
      { feature: "Referral system + Wall of Wins social-proof feed", pj: true, them: false },
      { feature: "Programmatic SEO (160+ niche landing pages + keyword templates)", pj: true, them: false },
      { feature: "GEO/AEO citation tracker (ChatGPT/Perplexity/Gemini visibility)", pj: true, them: false },
      { feature: "Embeddable user badges for SEO backlinks", pj: true, them: false },
      { feature: "Print-ready flyers & menus PDFs", pj: true, them: true, note: "Taka's original strength — Creatara shipped brand-kit-aware flyer/menu PDFs in June 2026" },
      { feature: "White-label workspaces for agencies", pj: true, them: false },
      { feature: "Free forever plan", pj: true, them: false, note: "Taka is 7-day trial only" },
    ],
    pj_wins: [
      "Auto-publish to X, LinkedIn, Bluesky & Telegram at the scheduled time — you walk away, it ships",
      "Affiliate links + UTM attribution panel — see exactly which platform drove a sale",
      `Free forever plan with ${CLAIMS.freeAllowance.generations} generations/${CLAIMS.freeAllowance.period} + referrals; Taka is 7-day trial only`,
      "Sora 2 video, podcast clip reels, citation tracker — Taka has none of these",
    ],
    them_wins: [
      "Pioneered the Brand Kit approach to on-brand small-business content — we adopted it later",
      "Simpler surface area: if all you need is on-brand content for a local business, Taka has less to learn",
    ],
    cta_headline: "Try the AI tool built to monetise your content, not just write it",
    cta_sub: `${CLAIMS.freeAllowance.generations} free generations/${CLAIMS.freeAllowance.period}. No card. Built for solopreneurs who care about revenue, not just reach.`,
  },
};

export default function VsCompetitorPage() {
  const { slug } = useParams();
  const data = COMPETITORS[slug];

  const meta = useMemo(() => ({
    title: data
      ? `Creatara vs ${data.name}: Honest 2026 Comparison`
      : "Creatara Comparisons",
    description: data
      ? `Honest, side-by-side comparison of Creatara vs ${data.name}. ${data.one_liner}`
      : "Pick the right AI content tool for your business.",
  }), [data]);

  if (!data) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Comparison not found</p>
          <Link to="/" className="text-violet-500 underline">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="vs-page">
      <Helmet>
        <title>{`${meta.title} | Creatara`}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={`https://creatara.ai/vs/${slug}`} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={data.one_liner} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": meta.title,
          "description": meta.description,
          "datePublished": "2026-06-06",
          "author": { "@type": "Organization", "name": "Creatara" },
          "publisher": { "@type": "Organization", "name": "Creatara", "url": "https://creatara.ai" }
        })}</script>
      </Helmet>

      {/* Nav */}
      <nav className="border-b border-white/5 sticky top-0 z-40 bg-background/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-500" /> Creatara</Link>
          <div className="flex items-center gap-3">
            <Link to="/wins" className="text-sm text-muted-foreground hover:text-foreground hidden sm:inline">Live Wins</Link>
            <Link to="/leaderboard" className="text-sm text-muted-foreground hover:text-foreground hidden sm:inline">Leaderboard</Link>
            <Link to="/auth"><Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="vs-nav-signup">Start free</Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-5xl mx-auto px-6 pt-14 pb-8">
        <Badge className="bg-violet-500/15 text-violet-400 border border-violet-500/30 mb-4">Honest comparison · Updated 2026</Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
          Creatara <span className="text-muted-foreground/60 font-normal">vs</span> <span className="text-violet-500">{data.name}</span>
        </h1>
        <p className="text-base sm:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-6">{data.intro}</p>
        <blockquote className="border-l-2 border-violet-500/50 pl-4 italic text-sm sm:text-base text-foreground/80 max-w-3xl">{data.summary_quote}</blockquote>
      </header>

      {/* Feature table */}
      <section className="max-w-5xl mx-auto px-6 mb-14">
        <h2 className="text-2xl font-bold mb-6">Side-by-side feature comparison</h2>
        <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="vs-table">
          <div className="grid grid-cols-12 bg-white/5 text-xs uppercase tracking-wider text-muted-foreground">
            <div className="col-span-6 px-4 py-3 font-semibold">Feature</div>
            <div className="col-span-3 px-4 py-3 text-center font-semibold text-violet-400">Creatara</div>
            <div className="col-span-3 px-4 py-3 text-center font-semibold">{data.name}</div>
          </div>
          {data.table.map((row, i) => (
            <div key={i} className="grid grid-cols-12 border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`vs-row-${i}`}>
              <div className="col-span-6 px-4 py-3 text-sm">
                {row.feature}
                {row.note && <p className="text-[11px] text-muted-foreground/60 mt-0.5">{row.note}</p>}
              </div>
              <div className="col-span-3 px-4 py-3 flex justify-center items-center">
                {row.pj ? <Check className="w-5 h-5 text-teal-400" /> : <X className="w-5 h-5 text-violet-500/50" />}
              </div>
              <div className="col-span-3 px-4 py-3 flex justify-center items-center">
                {row.them ? <Check className="w-5 h-5 text-teal-400/70" /> : <X className="w-5 h-5 text-violet-500/50" />}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Wins summary */}
      <section className="max-w-5xl mx-auto px-6 mb-14 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-teal-500/30 bg-teal-500/5 p-6" data-testid="pj-wins">
          <p className="text-xs uppercase tracking-wider text-teal-400 mb-3 font-semibold">Where Creatara wins</p>
          <ul className="space-y-2.5">
            {data.pj_wins.map((w, i) => (
              <li key={i} className="flex gap-2 text-sm"><Check className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />{w}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="them-wins">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold">Where {data.name} wins</p>
          <ul className="space-y-2.5">
            {data.them_wins.map((w, i) => (
              <li key={i} className="flex gap-2 text-sm"><Check className="w-4 h-4 text-foreground/40 flex-shrink-0 mt-0.5" />{w}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why-it-matters strip */}
      <section className="max-w-5xl mx-auto px-6 mb-16">
        <h2 className="text-2xl font-bold mb-6">Why the difference matters</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <Zap className="w-6 h-6 text-amber-400 mb-3" />
            <h3 className="font-semibold mb-1">Time to publish</h3>
            <p className="text-sm text-muted-foreground">Taka exports → you paste. Creatara auto-publishes to X, LinkedIn, Bluesky & Telegram at the scheduled time. The 30-second copy-paste-publish task is gone.</p>
          </div>
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <BarChart3 className="w-6 h-6 text-cyan-400 mb-3" />
            <h3 className="font-semibold mb-1">Revenue tracking</h3>
            <p className="text-sm text-muted-foreground">Every post you share via Creatara auto-stamps platform attribution. You'll know "LinkedIn drove 62% of my affiliate clicks this month" — not just "I posted."</p>
          </div>
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <ShieldCheck className="w-6 h-6 text-teal-400 mb-3" />
            <h3 className="font-semibold mb-1">Discoverability</h3>
            <p className="text-sm text-muted-foreground">160+ niche landing pages + GEO/AEO tracker mean Creatara gets cited in ChatGPT and ranks for the long-tail. Taka has no public SEO footprint.</p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-20 text-center">
        <div className="p-8 rounded-3xl bg-gradient-to-br from-violet-600/20 to-violet-700/5 border border-violet-500/30">
          <h3 className="text-3xl font-bold mb-3">{data.cta_headline}</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-xl mx-auto">{data.cta_sub}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/auth"><Button size="lg" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="vs-cta-signup">Start free <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/wins"><Button size="lg" variant="outline" className="rounded-full" data-testid="vs-cta-wins">See live user wins</Button></Link>
          </div>
        </div>
        <div className="mt-8 text-xs text-muted-foreground">
          Want a comparison for another tool? <a href="mailto:hello@creatara.ai" className="underline inline-flex items-center gap-1"><Mail className="w-3 h-3" />tell us which</a>
        </div>
      </section>
    </div>
  );
}

export { COMPETITORS };
